import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, ViewChild } from "@angular/core"
import { MatDialog } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { Linqset, LinqsetNode, getInsights, INSIGHTS_FIELD, NODE_SUPERTYPE, getWarningsForNode, NodeProperties } from "../../common/linqset-model";
import { FrequencyDialogComponent, FrequencyDialogData, FrequencyDialogResult } from "../../model-components/tempo-dialog/frequency-dialog.component";
import { CHANGE_TYPE } from "../../common/linqset-change-model";
import { NodeService } from "../../model-services/node.service";
import { MatSort } from "@angular/material/sort";
import { InsightsFieldType } from "../../insights/model/insights-field-type";
import { NodeRowData } from "./node-row-data";
import { NODE_TYPE_DATA_TYPE, NodeTypeData } from "../node-types/node-type-data";
import { GraphProcessingService } from "../../model-services/graph-processing.service";
import { User } from "../../common/user";
import { UserService } from "../../user/user.service";

@Component({
    selector: 'app-basic-node-table',
    templateUrl: './basic-node-table.html',
    styleUrls: ['./basic-node-table.css'],
    standalone: false
})
export class BasicNodeTableComponent implements OnChanges {
    @Input() linqset: Linqset
    @Input() typeData:NodeTypeData
    @Input() field:INSIGHTS_FIELD
    @Input() includeCarbon:boolean

    @Output() selectNodeEvent = new EventEmitter<string>();
    
    nodeColumns = ['icon', 'name'];
    nodeColumnHeaders = ['Type', 'Name'];

    loading:boolean = false
    insightColumns: InsightsFieldType[] = []
    columns = []
    headings = []
    datasource = new MatTableDataSource<NodeRowData>();
    @ViewChild(MatSort) sort: MatSort

    constructor(public nodeService:NodeService, public dialog: MatDialog, private graphProcessingService:GraphProcessingService, private userService:UserService, private changeDetectorRef:ChangeDetectorRef) {

    }

    ngOnChanges(changes) {
        if ((changes["linqset"] || changes["typeData"] || changes["field"]) && this.linqset) {
            this.loading = true
            this.changeDetectorRef.detectChanges()
            setTimeout(() => {
                try {
                    this.populateTable()
                } finally {
                    this.loading = false
                }
            }, 0)
        }
    }

    populateTable(): void {
        const isOutput = this.typeData.dataType === NODE_TYPE_DATA_TYPE.OUTPUT
        const isInput = this.typeData.dataType === NODE_TYPE_DATA_TYPE.INPUT

        this.columns = this.nodeColumns
        this.headings = this.nodeColumnHeaders
        this.insightColumns = []
        const user = this.userService.getUser()

        if (isInput) {
            if (this.linqset.isOrganisationModel()) {
                this.columns = this.nodeColumns.concat(["frequency", "model"])
                this.headings = this.nodeColumnHeaders.concat(["Frequency", "Model"])
            }else {
                this.columns = this.nodeColumns.concat(["frequency", "information"])
                this.headings = this.nodeColumnHeaders.concat(["Frequency", "Information"])    
            }
        }else if (isOutput) {
            if (this.linqset.isOrganisationModel()) {
                this.columns = this.nodeColumns.concat(["model"])
                this.headings = this.nodeColumnHeaders.concat(["Model"])
            }else {
                this.columns = this.nodeColumns.concat(["information"])
                this.headings = this.nodeColumnHeaders.concat(["Information"])
            }
        }else {
            this.columns = this.nodeColumns
            this.headings = this.nodeColumnHeaders
        }

        if (this.field && this.field !== INSIGHTS_FIELD.NONE) 
            this.insightColumns.push(new InsightsFieldType(this.field, this.linqset.modelKey, this.linqset.workspaceKey))

        const potentialColumns = [new InsightsFieldType(INSIGHTS_FIELD.ANNUAL_HOURS, this.linqset.modelKey, this.linqset.workspaceKey), 
                new InsightsFieldType(INSIGHTS_FIELD.COST, this.linqset.modelKey, this.linqset.workspaceKey), 
                new InsightsFieldType(INSIGHTS_FIELD.COST_ITEM, this.linqset.modelKey, this.linqset.workspaceKey), 
                new InsightsFieldType(INSIGHTS_FIELD.PROCESSED, this.linqset.modelKey, this.linqset.workspaceKey), 
                new InsightsFieldType(INSIGHTS_FIELD.VALUE, this.linqset.modelKey, this.linqset.workspaceKey), 
                new InsightsFieldType(INSIGHTS_FIELD.CAPACITY, this.linqset.modelKey, this.linqset.workspaceKey)
            ]

        if (this.includeCarbon) potentialColumns.push(new InsightsFieldType(INSIGHTS_FIELD.CARBON, this.linqset.modelKey, this.linqset.workspaceKey, ))
        potentialColumns.forEach(pc => {
            if (this.field !== pc.type && pc.doesFieldTypeApplyForTypeData(this.typeData))
                this.insightColumns.push(pc)
        })            

        this.columns = this.columns.concat(this.insightColumns.map(ic => ic.type))
        this.headings = this.headings.concat(this.insightColumns.map(ic => ic.type))

        if (this.typeData.dataType === NODE_TYPE_DATA_TYPE.MODELS) {
            this.datasource.data = this.typeData.models.map(m => {
                const n = {_key:m._key, name:m.name} as LinqsetNode
                const insights = m.insights?.find(i => i.projectKey === this.linqset.workspaceKey)
                return new NodeRowData(n, m._key, insights, this.typeData.dataType, this.insightColumns, [], [])
            })
        }else if (this.typeData.dataType === NODE_TYPE_DATA_TYPE.GROUP) {
            this.datasource.data = this.typeData.groups.map(g => {
                const n = {_key:g._key, name:g.name} as LinqsetNode
                return new NodeRowData(n, this.linqset.modelKey, g.insights, this.typeData.dataType, this.insightColumns, [], [])
            })
        }else {
            this.datasource.data = this.typeData.list.map(r => this.createNodeRow(r, user))
        }

        // sorting is done by sortChange, which knows where each column's value is
        if (this.field) this.sortChange({active:this.field.toString(), direction:"desc"})
    }

    createNodeRow(node:LinqsetNode, user:User) {
        let infoNames = node.superType===NODE_SUPERTYPE.INFORMATION?[node.name]:[]
        if (node.superType === NODE_SUPERTYPE.ACTION) {
            if (this.typeData.dataType === NODE_TYPE_DATA_TYPE.INPUT) {
                infoNames = this.linqset.edges.filter(e => e.sourceKey === node._key).map(e => {
                    const info = this.linqset.nodes.find(i => i._key === e.targetKey)
                    return info?info.name:""
                })
            }else if (this.typeData.dataType === NODE_TYPE_DATA_TYPE.OUTPUT) {
                infoNames = this.linqset.edges.filter(e => e.targetKey === node._key).map(e => {
                    const info = this.linqset.nodes.find(i => i._key === e.sourceKey)
                    return info?info.name:""
                })                
            }
        }
        const insights = getInsights(node, this.linqset)
        const model = this.linqset.modelKey === node.modelKey?this.linqset.model:this.linqset.organisation.models.find(m => m._key === node.modelKey)
        
        const row = new NodeRowData(node, model?._key || node.modelKey, insights, this.typeData.dataType, this.insightColumns, getWarningsForNode(node, this.linqset, user.preferences?.fieldTypes), infoNames)
        row.model = model
        return row
    }

    selectRow(row:NodeRowData) {
        this.selectNodeEvent.emit(row.node._key)
    }

    sortChange(event:{active:string, direction:string}) {
        if (event.direction === '') return
        const value = (row:NodeRowData):any => {
            if (event.active in row.insightsValues) return row.insightsValues[event.active] || 0
            const v:any = row[event.active]
            if (v && typeof v === 'object') return (v.name || '').toLowerCase()
            return typeof v === 'string' ? v.toLowerCase() : (v ?? '')
        }
        const direction = event.direction === 'asc' ? 1 : -1
        this.datasource.data = [...this.datasource.data].sort((a, b) => {
            const av = value(a), bv = value(b)
            return av === bv ? 0 : (av > bv ? direction : -direction)
        })
    }
   
    openFrequencyDialog(row:NodeRowData) {
        const {node} = row
        const data:FrequencyDialogData = {properties:node.properties, superType:node.superType, type:node.type, readonly:this.linqset?.readonly}

        const dialogRef = this.dialog.open(FrequencyDialogComponent, {data, width:'610px', hasBackdrop:true });
        dialogRef.afterClosed().subscribe((result:FrequencyDialogResult|undefined) => {
            if (result)  {
                if (!node.properties) node.properties = {} as NodeProperties
                if (result.durationChange) {
                    node.properties.duration = result.duration
this.graphProcessingService.createLinqsetNodeChangeEvent(node, CHANGE_TYPE.CHANGED, false, {_key:node._key, properties:{duration:result.duration}})
                }
                if (result.frequencyChange) {
                    node.properties.frequency = result.frequency
                    this.graphProcessingService.createLinqsetNodeChangeEvent(node, CHANGE_TYPE.CHANGED, false, {_key:node._key, properties:{frequency:result.frequency}})
                }
            }
        })
    }

}
